import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ViewportScroller, isPlatformBrowser } from '@angular/common';

/**
 * Custom ViewportScroller that targets the `#main-scroll` element instead of
 * the browser window. Registering this as the ViewportScroller provider enables
 * Angular's built-in `scrollPositionRestoration: 'enabled'` to work correctly
 * with the app's fixed-sidebar layout.
 */
@Injectable()
export class MainScrollViewportScroller implements ViewportScroller {
  private platformId = inject(PLATFORM_ID);
  private offset: () => [number, number] = () => [0, 0];

  private get scroller(): HTMLElement | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    return document.querySelector<HTMLElement>('#main-scroll');
  }

  setOffset(offset: [number, number] | (() => [number, number])): void {
    this.offset = typeof offset === 'function' ? offset : () => offset;
  }

  getScrollPosition(): [number, number] {
    const el = this.scroller;
    return el ? [el.scrollLeft, el.scrollTop] : [0, 0];
  }

  scrollToPosition(position: [number, number]): void {
    this.scroller?.scrollTo({ left: position[0], top: position[1], behavior: 'auto' });
  }

  scrollToAnchor(anchor: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const element =
      document.getElementById(anchor) ??
      document.querySelector<HTMLElement>(`[name="${anchor}"]`);
    if (!element) return;
    const container = this.scroller;
    if (!container) return;
    const [, offsetY] = this.offset();
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const relativeTop = elementRect.top - containerRect.top + container.scrollTop;
    container.scrollTo({ top: Math.max(0, relativeTop - offsetY), behavior: 'smooth' });
  }

  setHistoryScrollRestoration(scrollRestoration: 'auto' | 'manual'): void {
    if (isPlatformBrowser(this.platformId) && history.scrollRestoration) {
      history.scrollRestoration = scrollRestoration;
    }
  }
}
